import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createAdminDepartment } from '../api';
import Button from './ui/Button';

const initialFormState = { name: '', description: '' };

function AdminCreateDepartmentForm({ onCreated }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function updateField(field) {
    return (event) => {
      setForm((current) => ({
        ...current,
        [field]: event.target.value,
      }));

      setError('');
      setSuccess('');
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const name = form.name.trim();

    if (name.length < 2) {
      setError(t('adminPanel.errorDepartmentName'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const created = await createAdminDepartment({
        name,
        description: form.description.trim() || undefined,
      });

      setForm(initialFormState);
      setSuccess(t('adminPanel.departmentCreated', { name: created.name }));

      if (onCreated) {
        onCreated(created);
      }
    } catch (err) {
      // A duplicate name answers 409; anything else keeps the server's message.
      setError(
        err?.status === 409
          ? t('adminPanel.errorDepartmentExists')
          : err?.message || t('adminPanel.errorGeneric'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="account-form" onSubmit={handleSubmit}>
      <div className="account-field-row">
        <label htmlFor="adminNewDepartmentName">
          {t('adminPanel.departmentNameLabel')}
        </label>

        <input
          id="adminNewDepartmentName"
          name="departmentName"
          type="text"
          value={form.name}
          disabled={isSubmitting}
          onChange={updateField('name')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewDepartmentDescription">
          {t('adminPanel.departmentDescriptionLabel')}
        </label>

        <input
          id="adminNewDepartmentDescription"
          name="departmentDescription"
          type="text"
          value={form.description}
          disabled={isSubmitting}
          onChange={updateField('description')}
        />
      </div>

      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{success}</span>
        </div>
      )}

      <div className="account-actions">
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? t('adminPanel.creating')
            : t('adminPanel.createDepartmentSubmit')}
        </Button>
      </div>
    </form>
  );
}

export default AdminCreateDepartmentForm;
